



import React, { createContext, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../api';
import { formatLocalDate } from '../utils/dateUtils';

export const RoomContext = createContext();

const getDefaultStayDates = () => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  return {
    checkInDate: formatLocalDate(today),
    checkOutDate: formatLocalDate(tomorrow),
  };
};

const parseGuestCount = (value) => parseInt(String(value).split(' ')[0], 10) || 0;

const calculateNights = (checkInDate, checkOutDate) => {
  const checkIn = new Date(`${checkInDate}T12:00:00`);
  const checkOut = new Date(`${checkOutDate}T12:00:00`);
  const nights = Math.round((checkOut - checkIn) / (1000 * 3600 * 24));
  return nights > 0 ? nights : 0;
};

export const RoomProvider = ({ children }) => {
  const [rooms, setRooms] = useState([]);
  const [adults, setAdults] = useState('1 Adult');
  const [kids, setKids] = useState('0 Kids');
  const [totalGuests, setTotalGuests] = useState(0);
  const [loading, setLoading] = useState(true);
  const defaultStayDates = getDefaultStayDates();

  const [selectedRoomDetails, setSelectedRoomDetails] = useState({
    checkInDate: defaultStayDates.checkInDate,
    checkOutDate: defaultStayDates.checkOutDate,
    roomType: '',
    adults: 1,
    kids: 0,
    price: 0,
    mealPlan: null, // Added meal plan
    mealCost: 0, // Added meal cost
    taxes: 0,
    totalAmount: 0,
    numberOfDays: 1, // Default to 1 day
  });

  const fetchAvailableRooms = useCallback(
    async ({ checkInDate, checkOutDate, adultsCount, kidsCount, showLoader = true } = {}) => {
      const safeCheckInDate = checkInDate || selectedRoomDetails.checkInDate;
      const safeCheckOutDate = checkOutDate || selectedRoomDetails.checkOutDate;
      const safeAdultsCount = adultsCount ?? parseGuestCount(adults);
      const safeKidsCount = kidsCount ?? parseGuestCount(kids);
      const totalGuestCount = safeAdultsCount + safeKidsCount;
      const numberOfDays = calculateNights(safeCheckInDate, safeCheckOutDate);

      if (numberOfDays < 1) {
        throw new Error('Check-out date must be later than check-in date.');
      }

      if (showLoader) setLoading(true);

      try {
        const response = await axios.get(`${API_BASE_URL}/inventory/available`, {
          params: {
            checkIn: safeCheckInDate,
            checkOut: safeCheckOutDate,
            maxPerson: totalGuestCount || 1,
            _: Date.now(),
          },
          timeout: 15000,
        });
        const availableRooms = response.data || [];

        setRooms(availableRooms);
        setSelectedRoomDetails((prevDetails) => ({
          ...prevDetails,
          checkInDate: safeCheckInDate,
          checkOutDate: safeCheckOutDate,
          adults: safeAdultsCount,
          kids: safeKidsCount,
          numberOfDays,
        }));

        return { rooms: availableRooms, numberOfDays };
      } finally {
        if (showLoader) setLoading(false);
      }
    },
    [adults, kids, selectedRoomDetails.checkInDate, selectedRoomDetails.checkOutDate]
  );

  // Fetch available room data from the backend API
  useEffect(() => {
    fetchAvailableRooms({
      checkInDate: defaultStayDates.checkInDate,
      checkOutDate: defaultStayDates.checkOutDate,
      adultsCount: parseGuestCount(adults),
      kidsCount: parseGuestCount(kids),
    }).catch((error) => {
      console.error('Error fetching available room data:', error);
      setRooms([]);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update total number of guests when adults or kids change
  useEffect(() => {
    const numberOfAdults = parseInt(adults.split(' ')[0], 10);
    const numberOfKids = parseInt(kids.split(' ')[0], 10);
    setTotalGuests(numberOfAdults + numberOfKids);
  }, [adults, kids]);

  const handleClick = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    try {
      await fetchAvailableRooms();
    } catch (error) {
      alert(error.message || 'Could not check availability. Please try again.');
      setLoading(false);
    }
  };

  // Update selected room details with new properties
  const updateRoomDetails = (details) => {
    setSelectedRoomDetails((prevDetails) => ({
      ...prevDetails,
      ...details,
    }));
  };

  return (
    <RoomContext.Provider
      value={{
        rooms,
        setRooms,
        adults,
        setAdults,
        kids,
        setKids,
        totalGuests,
        handleClick,
        loading,
        setLoading,
        fetchAvailableRooms,
        selectedRoomDetails,
        setSelectedRoomDetails,
        updateRoomDetails,
      }}
    >
      {children}
    </RoomContext.Provider>
  );
};
